import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { AlertMessage } from './ui/AlertMessage';
import { Button } from './ui/Button';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { API_BASE_URL } from '../api/mediaUrl';

function sortDesignsRecentFirst(list) {
  return [...list].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return String(b.id).localeCompare(String(a.id));
  });
}

/**
 * Concept card: ~70% image area, ~30% label (matches reference layout).
 */
function ConceptCard({ label, coverSrc, selected, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'flex flex-col w-full rounded-xl overflow-hidden bg-white border text-left transition-all',
        'shadow-md hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        'aspect-[5/7] max-h-[280px]',
        selected ? 'ring-2 ring-primary-500 border-primary-300' : 'border-gray-200',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      <div className="relative flex-[7] min-h-0 w-full bg-gray-100">
        {coverSrc ? (
          <AuthenticatedImage
            src={coverSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400 text-[10px] uppercase tracking-wide">
            No preview
          </div>
        )}
      </div>
      <div className="flex-[3] flex min-h-0 items-center justify-center px-2 py-2 border-t border-gray-100 bg-white">
        <span className="text-[10px] sm:text-[11px] font-semibold text-gray-800 uppercase tracking-wide text-center leading-tight line-clamp-3">
          {label}
        </span>
      </div>
    </button>
  );
}

/** Large image-only tile in gallery */
function GalleryImage({ src, onClick }) {
  if (!src) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="aspect-[3/4] rounded-lg overflow-hidden border border-gray-200 bg-gray-100 shadow-sm hover:ring-2 hover:ring-primary-400 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <AuthenticatedImage src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
    </button>
  );
}

/**
 * Products:
 * — Concepts: "All Products", then all categories.
 * — Click All Products → all designs (images only), newest → oldest.
 * — Click category → that category’s designs only.
 */
function Products() {
  const [designs, setDesigns] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  /** null = concepts only; 'all' | category id */
  const [selectedKey, setSelectedKey] = useState(null);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const [designRes, catRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/designs`, { headers }),
        fetch(`${API_BASE_URL}/api/insights?type=CATEGORY`, { headers }),
      ]);
      if (!designRes.ok) throw new Error('Unable to load designs');
      if (!catRes.ok) throw new Error('Unable to load categories');
      const designData = await designRes.json();
      const catData = await catRes.json();
      setDesigns(Array.isArray(designData) ? designData : []);
      setCategoryOptions(Array.isArray(catData) ? catData : []);
    } catch (e) {
      setPageError(e?.message || 'Failed to load products');
      setDesigns([]);
      setCategoryOptions([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const withImages = useMemo(
    () => designs.filter((d) => String(d.designImage || '').trim()),
    [designs]
  );

  const conceptCategoryRows = useMemo(() => {
    const byId = new Map();
    categoryOptions.forEach((c) => {
      if (c?.id == null) return;
      const id = String(c.id);
      byId.set(id, { id, name: String(c.name || '').trim() || `Category ${id}` });
    });
    withImages.forEach((d) => {
      if (d.categoryId == null) return;
      const id = String(d.categoryId);
      if (!byId.has(id)) {
        byId.set(id, { id, name: String(d.categoryName || '').trim() || `Category ${id}` });
      }
    });
    return Array.from(byId.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [categoryOptions, withImages]);

  const coverForCategory = useCallback(
    (categoryId) => {
      const list = withImages.filter((d) => String(d.categoryId) === String(categoryId));
      const first = sortDesignsRecentFirst(list)[0];
      return first?.designImage || '';
    },
    [withImages]
  );

  const allProductsCover = useMemo(() => {
    const first = sortDesignsRecentFirst(withImages)[0];
    return first?.designImage || '';
  }, [withImages]);

  const galleryDesigns = useMemo(() => {
    if (selectedKey === null) return [];
    if (selectedKey === 'all') return sortDesignsRecentFirst(withImages);
    return sortDesignsRecentFirst(withImages.filter((d) => String(d.categoryId) === String(selectedKey)));
  }, [selectedKey, withImages]);

  const galleryTitle = useMemo(() => {
    if (selectedKey === null) return '';
    if (selectedKey === 'all') return 'All products';
    return conceptCategoryRows.find((c) => String(c.id) === String(selectedKey))?.name || 'Category';
  }, [selectedKey, conceptCategoryRows]);

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar tone="light" title="Products" />
        </CardHeader>
        <CardBody className="pt-6 space-y-8">
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-2" />
          {loading ? (
            <div className="py-16 text-center text-gray-500">Loading…</div>
          ) : (
            <>
              <section>
                <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide mb-4">Concepts</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  <ConceptCard
                    label="All Products"
                    coverSrc={allProductsCover}
                    selected={selectedKey === 'all'}
                    onClick={() => setSelectedKey('all')}
                  />
                  {conceptCategoryRows.map((cat) => (
                    <ConceptCard
                      key={cat.id}
                      label={cat.name}
                      coverSrc={coverForCategory(cat.id)}
                      selected={selectedKey === cat.id}
                      onClick={() => setSelectedKey(cat.id)}
                    />
                  ))}
                </div>
              </section>

              {selectedKey !== null && (
                <section className="border-t border-gray-100 pt-8">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{galleryTitle}</h2>
                    <Button type="button" variant="outline" size="sm" onClick={() => setSelectedKey(null)}>
                      Back to concepts
                    </Button>
                  </div>
                  {galleryDesigns.length === 0 ? (
                    <p className="text-sm text-gray-400">No design images in this selection.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {galleryDesigns.map((d) => (
                        <GalleryImage key={d.id} src={d.designImage} onClick={() => setLightbox({ src: d.designImage })} />
                      ))}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {lightbox?.src && (
        <button
          type="button"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 border-0 cursor-default bg-slate-900/55 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          aria-label="Close"
        >
          <AuthenticatedImage
            src={lightbox.src}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          />
        </button>
      )}
    </div>
  );
}

export default Products;
